import type { ReactNode } from 'react';

/* ════════════════════════════════════════════════════════════════════════════
   AVISOS · una sola definición de color para TODO el portal

   El aviso que sale después de una acción («Negociación #63 anulada…»,
   «Informe guardado», «No se pudo cargar el archivo») estaba escrito cuatro
   veces —Negociaciones, Negociaciones Farmatodo, Inactivación y el Simulador—
   y las cuatro copias pintaban el texto con el tono `emerald-200`, o sea el verde
   MENTA de Tailwind. Ese tono nació para fondo oscuro. El portal arranca en
   tema claro (`index.html` pone `data-theme="light"`), así que el aviso salía
   menta pálido sobre un fondo verde al 10 % sobre blanco:

       contraste medido 1,17 : 1     ·  el mínimo legible (WCAG AA) es 4,5 : 1

   No era «poco contraste»: era texto invisible. Lo mismo pasaba en ámbar
   (1,15), rosa (1,24) y naranja (1,22).

   ── La regla, y no admite excepciones ──────────────────────────────────────
   Sobre cualquier superficie teñida (`bg-<familia>-500/5` … `/20`):

       text-<familia>-800   dark:text-<familia>-200

   Medido en las seis familias que usa el portal y en los seis fondos que
   existen en el código, el PEOR caso da 5,95 : 1 en claro y 9,38 : 1 en
   oscuro. Todos pasan AA y casi todos pasan AAA (7 : 1). El par es simétrico
   a propósito: el 800 es el 200 «dado vuelta», así que el aviso pesa lo mismo
   en los dos temas y no hay que volver a decidir nada al agregar un módulo.

   NO usar 200/300/400 sin el prefijo `dark:`. Hay una prueba que lo vigila:
   `componentes/avisos.test.ts` recorre el código y falla si reaparece.

   ── Por qué arriba dice «el tono `emerald-200`» y no la clase entera ────────
   Porque Tailwind rastrea estos archivos como TEXTO PLANO: no distingue un
   comentario de un `className`. Escribir la clase completa aquí, para
   explicar que ya no se usa, hacía que el compilador la emitiera igual — una
   regla muerta en la hoja de producción que no pinta nada, pero que engaña a
   quien luego mire el CSS para comprobar si el tono malo desapareció. Pasó, y
   el falso positivo apareció en la comprobación del despliegue. Al nombrar el
   tono sin el prefijo `text-` deja de ser una clase y la hoja queda limpia.
   ════════════════════════════════════════════════════════════════════════════ */

export type NivelAviso = 'ok' | 'mid' | 'bad';

/** Banda de aviso: borde + fondo teñido + tinta legible en los dos temas. */
export const CLASE_SEM: Record<NivelAviso, string> = {
  ok:  'border-emerald-500/40 bg-emerald-500/10 text-emerald-800 dark:text-emerald-200',
  mid: 'border-amber-500/40 bg-amber-500/10 text-amber-800 dark:text-amber-200',
  bad: 'border-rose-500/40 bg-rose-500/10 text-rose-800 dark:text-rose-200',
};

/* Píldoras de estado (las etiquetitas de las tablas). Mismo par de tinta, con
   el fondo un punto más marcado porque la píldora es pequeña y va suelta. */
export const CLASE_PILDORA = {
  ok:      'bg-emerald-500/15 text-emerald-800 dark:text-emerald-200',
  mid:     'bg-amber-500/15 text-amber-800 dark:text-amber-200',
  bad:     'bg-rose-500/15 text-rose-800 dark:text-rose-200',
  rechazo: 'bg-orange-500/15 text-orange-800 dark:text-orange-200',
  info:    'bg-blue-500/15 text-blue-800 dark:text-blue-200',
  neutro:  'bg-suave text-texto-2',
} as const;

/**
 * La banda de aviso. Sale después de una acción y cuenta qué pasó, así que
 * lleva `role="status"`: quien navega con lector de pantalla se entera de que
 * la anulación se hizo sin tener que ir a buscarlo. El error va en `alert`,
 * que interrumpe; el resto en `status`, que espera un hueco.
 */
export function Aviso({ nivel, children, className = '' }: {
  nivel: NivelAviso; children: ReactNode; className?: string;
}) {
  return (
    <div
      role={nivel === 'bad' ? 'alert' : 'status'}
      className={`rounded-xl border px-3 py-2 text-xs leading-relaxed ${CLASE_SEM[nivel]} ${className}`}
    >
      {children}
    </div>
  );
}

/** Nombre que usaban Negociaciones, Farmatodo y el Simulador. */
export const Semaforo = Aviso;

export default Aviso;
